import { useEffect, useState } from "react";
import {
  Button,
  Input,
  Modal,
  Numeric,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import type { ProductDetailDTO, VolumeBreakDTO } from "@wmart/shared-types";

/**
 * Volume breaks editor for a single variant. Rendered as a `<Modal>` so the
 * caller (ProductEdit) can open/close it like the other admin modals.
 *
 * Refactored onto `<Modal>` per P11-T11 — ESC + click-outside + focus-trap +
 * scroll-lock all come from the primitive. Plan 11 did not explicitly list
 * this as one of the four modals, but it shared the bare overlay pattern
 * with them and is now unified.
 */
export function VolumeBreaksEditor({
  variantId,
  productId,
  onClose,
}: {
  variantId: string;
  productId: string;
  onClose: () => void;
}) {
  const { notify, notifyProblem } = useToast();
  const [breaks, setBreaks] = useState<VolumeBreakDTO[]>([]);
  const [minQty, setMinQty] = useState("");
  const [unitMinor, setUnitMinor] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const product = await api.get<ProductDetailDTO>(
        `/v1/admin/products/${productId}`,
      );
      const variant = product.variants.find((v) => v.id === variantId);
      setBreaks(variant?.volume_breaks ?? []);
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantId, productId]);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(
        `/v1/admin/products/${productId}/variants/${variantId}/volume-breaks`,
        {
          min_quantity: Number(minQty),
          unit_price_minor: Number(unitMinor),
        },
      );
      notify("success", "Volume break added");
      setMinQty("");
      setUnitMinor("");
      void load();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Add failed");
    } finally {
      setBusy(false);
    }
  }

  async function del(id: string) {
    if (!confirm("Delete this volume break?")) return;
    try {
      await api.delete(
        `/v1/admin/products/${productId}/variants/${variantId}/volume-breaks/${id}`,
      );
      notify("success", "Volume break deleted");
      void load();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Volume breaks"
      size="md"
      footer={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        {breaks.length === 0 ? (
          <p className="py-2 text-center text-body-sm text-ink-500">
            No volume breaks yet.
          </p>
        ) : (
          <Table density="compact">
            <THead>
              <TR>
                <TH className="text-right">Min qty</TH>
                <TH className="text-right">Unit (minor)</TH>
                <TH className="text-right">Actions</TH>
              </TR>
            </THead>
            <TBody>
              {breaks.map((b) => (
                <TR key={b.id}>
                  <TD className="text-right">
                    <Numeric>{b.min_quantity}</Numeric>
                  </TD>
                  <TD className="text-right">
                    {b.unit_price_minor} {b.currency}
                  </TD>
                  <TD className="text-right">
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => void del(b.id)}
                      className="text-danger"
                    >
                      Delete
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}

        <form onSubmit={add} className="flex items-end gap-2">
          <div className="flex-1">
            <Input
              required
              type="number"
              min={1}
              label="Min qty"
              value={minQty}
              onChange={(e) => setMinQty(e.target.value)}
            />
          </div>
          <div className="flex-1">
            <Input
              required
              type="number"
              min={0}
              label="Unit (minor)"
              value={unitMinor}
              onChange={(e) => setUnitMinor(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={busy} loading={busy}>
            Add
          </Button>
        </form>
      </div>
    </Modal>
  );
}
